/**
 * Helper functions for COIT REST app.
 */

import ConditionValidator from '../api/cart/ConditionValidator';

// Create a shared ConditionValidator instance
const conditionValidator = new ConditionValidator(console);

/**
 * Scrolls the page to the top of the #app element, accounting for a fixed header offset.
 */
export const scrollToTopOfApp = () => {
  requestAnimationFrame(() => {
    const app = document.getElementById("app");
    if (app) {
      const appTop = app.getBoundingClientRect().top + window.pageYOffset;
      const offset = 210;
      // Scroll to the top of app
      window.scrollTo({ top: Math.max(0, appTop - offset), behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  });
};

/**
 * Build a Date from a YYYY-MM-DD string in local time, avoiding a UTC shift.
 *
 * @param {string} value - Date string in YYYY-MM-DD format
 * @param {boolean} endOfDay - Use 23:59:59 instead of midnight
 * @returns {Date|null} The parsed date, or null if unparseable
 */
export const parseLocalDate = (value, endOfDay = false) => {
  const [year, month, day] = String(value).split('-').map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return endOfDay
    ? new Date(year, month - 1, day, 23, 59, 59)
    : new Date(year, month - 1, day);
};

/**
 * Format a YYYY-MM-DD string for display, e.g. "August 31, 2026".
 *
 * @param {string} value - Date string in YYYY-MM-DD format
 * @returns {string} The formatted date, or the original value if unparseable
 */
export const formatDateForDisplay = (value) => {
  const date = parseLocalDate(value);

  if (!date) {
    return value;
  }

  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Check if a promotion's conditions are met based on current cart state
 * @param {Object} promo - Promotion object with conditions
 * @param {Object} cart - Cart object from useFormStore
 * @returns {boolean} True if promotion conditions are met
 */
export const checkPromotionConditions = (promo, cart) => {
  // If no conditions, promotion is always available
  if (!promo.conditions || Object.keys(promo.conditions).length === 0) {
    return true;
  }

  // Get cart data
  const categorySummary = cart?.categorySummary || {};

  if (Object.keys(categorySummary).length === 0) {
    return false;
  }

  // Check each condition using the validator's checkQuantityCondition method
  const conditionResults = Object.values(promo.conditions).map(condition => {
    // Find category data by service name or service_id
    let categoryData = categorySummary[condition.service];

    // If not found by service name, try by service_id
    if (!categoryData && condition.service_id) {
      categoryData = Object.values(categorySummary).find(category =>
        category.items && category.items.some(item =>
          String(item.category_id) === String(condition.service_id)
        )
      );
    }

    if (!categoryData) {
      return false;
    }

    const actualQuantity = categoryData.quantity || 0;
    const requiredQuantity = parseInt(condition.qty, 10) || 0;

    // Use the validator's quantity checking method
    return conditionValidator.checkQuantityCondition(
      actualQuantity,
      requiredQuantity,
      condition.condition
    );
  });

  // Check requirement: "all" or "some"
  if (promo.requirement === 'all') {
    return conditionResults.every(result => result === true);
  } else {
    return conditionResults.some(result => result === true);
  }
};
